import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    IconButton,
    Snackbar,
    Grid,
    Typography,
    CircularProgress
} from '@mui/material';
import MuiAlert, { AlertColor } from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation, gql } from '@apollo/client';
import { Dealer } from './dealers';

const EDIT_DEALER_MUTATION = gql`
  mutation EditDealer($dealerId: String!, $dealer: DealerInput!) {
    editDealer(dealerId: $dealerId, dealer: $dealer) {
      status
      message
    }
  }
`;

interface EditDealersDialogProps {
    open: boolean;
    onClose: () => void;
    dealer?: Dealer;
    onSaveSuccess?: (data: any) => void;
}

const EditDealersDialog: React.FC<EditDealersDialogProps> = ({ open, onClose, dealer, onSaveSuccess }) => {
    const [formData, setFormData] = useState(dealer);
    const [editDealerMutation, { loading, error }] = useMutation(EDIT_DEALER_MUTATION);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: AlertColor }>({ open: false, message: '', severity: 'success' });

    React.useEffect(() => {
        if (dealer) setFormData(dealer);
        setSnackbar({ open: false, message: '', severity: 'success' });
    }, [dealer]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => (prev ? { ...prev, [name]: value } as Dealer : prev));
    };

    const hasChanges = React.useMemo(() => {
        if (!formData || !dealer) return false;
        return formData.dealer_name !== dealer.dealer_name || formData.dealer_email !== dealer.dealer_email;
    }, [formData, dealer]);

    const isValid = React.useMemo(() => {
        return !!formData?.dealer_name?.trim() && !!formData?.dealer_email?.trim();
    }, [formData]);

    const handleSnackbarClose = () => setSnackbar(prev => ({ ...prev, open: false }));

    const handleSubmit = async () => {
        if (!formData) return;
        if (!isValid) {
            setSnackbar({ open: true, message: 'Dealer Name and Email cannot be empty.', severity: 'warning' });
            return;
        }
        if (!hasChanges) {
            setSnackbar({ open: true, message: 'No changes were made.', severity: 'info' });
            return;
        }

        try {
            setSnackbar({ open: true, message: 'Updating dealer details...', severity: 'info' });
            const { data } = await editDealerMutation({
                variables: {
                    dealerId: formData.dealer_id,
                    dealer: (({ __typename, ...rest }: any) => rest)(formData)
                }
            });

            const msg = data?.editDealer?.message || '';
            const success = data?.editDealer?.status === true ||
                data?.editDealer?.status === 'true' ||
                msg.toLowerCase().includes('success');

            if (success) {
                setSnackbar({ open: true, message: 'Dealer updated successfully!', severity: 'success' });
                setTimeout(() => {
                    onClose();
                    onSaveSuccess?.({ ...formData });
                }, 1500);
            } else {
                setSnackbar({ open: true, message: msg || 'Failed to update dealer', severity: 'error' });
            }
        } catch (err: any) {
            setSnackbar({ open: true, message: 'Error updating dealer', severity: 'error' });
            console.error('Error updating dealer:', err);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Typography variant="h6">Edit Dealer Details</Typography>
                <IconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8, color: '#000'}}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {error && <Typography color="error">Error: {error.message}</Typography>}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid sx={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Dealer Name"
                            name="dealer_name"
                            value={formData?.dealer_name || ''}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid sx={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Dealer Email"
                            name="dealer_email"
                            value={formData?.dealer_email || ''}
                            onChange={handleChange}
                        />
                    </Grid>
                </Grid>
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={3000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                    <MuiAlert
                        onClose={handleSnackbarClose}
                        severity={snackbar.severity}
                        sx={{ width: '100%' }}
                        elevation={6}
                        variant="filled"
                    >
                        {snackbar.message}
                    </MuiAlert>
                </Snackbar>
            </DialogContent>
            <DialogActions sx={{
                padding: '16px 24px',
                justifyContent: 'flex-end'
            }}>
                <Button onClick={onClose} variant="outlined">
                    Cancel

                </Button>
                <Button onClick={handleSubmit} variant="contained" disabled={loading || !hasChanges || !isValid}
                    color='error'
                >
                    {loading ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditDealersDialog;